import { InfoPage } from '@/components/info-page';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { EmptyArray, getAdminSessions, getSchool } from '@/lib/api-calls';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { usePaginatedFilters } from '@/lib/filtering';
import { classTimeTypesFilterOption, meetingStatusesFilterOption } from '@/lib/form-utils';
import { paginationRequestSchema } from '@/lib/pagination';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { useSorting } from '@/lib/sorting';
import { getStudentYearOptions } from '@/lib/student-utils';
import { BulkRefreshZoomLinks } from '@/routes/_layout/sessions/-components/bulk-refresh-zoom-links';
import { CancelSessions } from '@/routes/_layout/sessions/-components/cancel-sessions';
import { CreateSessionModal } from '@/routes/_layout/sessions/-components/create-session-modal';
import { RestoreSessions } from '@/routes/_layout/sessions/-components/restore-sessions';
import { SessionsTable } from '@/routes/_layout/sessions/-components/sessions-table';
import { FrequentTimeZones, isAirAdmin, plainDateFromStringOrElseUndefined } from '@airreading/common';
import {
  AdminSession,
  classTimeTypeSchema,
  GetAdminSessionsSortableColumn,
  getAdminSessionsSortOrderSchema,
  meetingStatusSchema,
  studentYearSchema,
} from '@airreading/common/data';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { z } from 'zod';
import { pluralize } from '@/lib/string-utils';
import { schoolDetailsTabs } from '../-components/tabs';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';

export const Route = createFileRoute('/_layout/orgs/schools/$schoolId/sessions')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: SchoolSessionsDetails,
  validateSearch: paginationRequestSchema.extend({
    query: z.string().optional(),
    grades: z.array(studentYearSchema).optional(),
    startOnOrAfterPlainDate: z.string().optional(),
    startOnOrBeforePlainDate: z.string().optional(),
    classTypes: z.array(classTimeTypeSchema).optional(),
    meetingStatuses: z.array(meetingStatusSchema).optional(),
    sortBy: getAdminSessionsSortOrderSchema.optional(),
  }),
});

function SchoolSessionsDetails() {
  const { schoolId } = Route.useParams();
  const { data } = useSuspenseQuery(getSchool(schoolId));
  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[query, activeQuery], setQuery],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      startOnOrAfterPlainDate: [[rawStartOnOrAfterPlainDate], setStartOnOrAfterPlainDate],
      startOnOrBeforePlainDate: [[rawStartOnOrBeforePlainDate], setStartOnOrBeforePlainDate],
      classTypes: [[selectedClassTypes, activeSelectedClassTypes], setSelectedClassTypes],
      meetingStatuses: [[selectedMeetingStatuses, activeSelectedMeetingStatuses], setSelectedMeetingStatuses],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, [
    'query',
    'grades',
    'startOnOrAfterPlainDate',
    'startOnOrBeforePlainDate',
    'classTypes',
    'meetingStatuses',
  ]);

  const startOnOrAfterPlainDate = plainDateFromStringOrElseUndefined(rawStartOnOrAfterPlainDate);
  const startOnOrBeforePlainDate = plainDateFromStringOrElseUndefined(rawStartOnOrBeforePlainDate);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetAdminSessionsSortableColumn>([sortBy, didChangeSortBy]);

  const {
    data: sessionsResp,
    isPending,
    error,
    refetch,
  } = useQuery(
    getAdminSessions({
      pageIndex,
      pageSize,
      sortBy,
      filters: {
        startOnOrAfter: startOnOrAfterPlainDate
          ? startOnOrAfterPlainDate.toZonedDateTime(FrequentTimeZones.UsCentral).startOfDay().toInstant().toString()
          : undefined,
        startOnOrBefore: startOnOrBeforePlainDate
          ? startOnOrBeforePlainDate
              .toZonedDateTime(FrequentTimeZones.UsCentral)
              .add({ days: 1 })
              .startOfDay()
              .subtract({ milliseconds: 1 })
              .toInstant()
              .toString()
          : undefined,
        query: activeQuery,
        schools: [schoolId],
        grades: activeSelectedGrades,
        classTypes: activeSelectedClassTypes,
        meetingStatuses: activeSelectedMeetingStatuses,
      },
    }),
  );
  const sessions = useMemo(() => sessionsResp?.adminSessions || EmptyArray, [sessionsResp]);
  const rowCount = sessionsResp?.total || 0;

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<AdminSession>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => sessions.find((session) => session.id === id),
    });

  const bulkActionsMenuItems = (
    <>
      <CancelSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Cancel session', 'Cancel sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <RestoreSessions
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Restore session', 'Restore sessions')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
      <BulkRefreshZoomLinks
        sessions={rowDataSelection}
        trigger={
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            {pluralize(rowDataSelection.length, 'Refresh zoom host link', 'Refresh zoom host links')}
          </DropdownMenuItem>
        }
        onSuccess={resetAllRowSelection}
      />
    </>
  );

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search sessions by student, email, or teacher"
          value={query ?? ''}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={rawStartOnOrAfterPlainDate}
          toPlainDateString={rawStartOnOrBeforePlainDate}
          onRangeSelect={(range) => {
            setStartOnOrAfterPlainDate(range.fromPlainDateString);
            setStartOnOrBeforePlainDate(range.toPlainDateString);
          }}
          placeholder="Date"
        />
        <TableFilterDropdown
          title="Grade"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Class type"
          options={classTimeTypesFilterOption}
          selectedValues={selectedClassTypes ?? []}
          onChange={setSelectedClassTypes}
        />
        <TableFilterDropdown
          title="Meeting status"
          options={meetingStatusesFilterOption}
          selectedValues={selectedMeetingStatuses ?? []}
          onChange={setSelectedMeetingStatuses}
        />
        <TableFilterResetButton show onClick={resetFilters} />
      </>
    ),
    [
      query,
      setStartOnOrAfterPlainDate,
      rawStartOnOrAfterPlainDate,
      setStartOnOrBeforePlainDate,
      rawStartOnOrBeforePlainDate,
      selectedGrades,
      setSelectedGrades,
      selectedClassTypes,
      setSelectedClassTypes,
      selectedMeetingStatuses,
      setSelectedMeetingStatuses,
      resetFilters,
      setQuery,
    ],
  );
  const topBarRightComponents = (
    <CreateSessionModal
      trigger={<Button>New session</Button>}
      preSelectedSchool={{
        value: schoolId,
        label: data.name,
      }}
    />
  );

  return (
    <InfoPage
      headerText={data.name || data.id}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Schools',
            link: {
              to: '/orgs/schools',
            },
          },
        ],
        currentPage: data.name,
      }}
      tabs={schoolDetailsTabs({ schoolId })}
    >
      <SessionsTable
        sessions={sessions}
        hasError={!!error}
        isLoading={isPending}
        errorState={<TableErrorState refetchAction={refetch} />}
        tableHookProps={{
          rowCount,
          state: {
            pagination: {
              pageIndex,
              pageSize,
            },
            rowSelection,
            sorting,
          },
          onRowSelectionChange,
          getRowId,
          onPaginationChange,
          ...tableSortingOptions,
        }}
        displayTimeZone={FrequentTimeZones.UsCentral}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
        from="school"
      />
    </InfoPage>
  );
}
